import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useOrganization } from "../context/OrganizationContext";
import { OrganizationSwitcher } from "./OrganizationSwitcher";
import "./TopBar.css";

interface TopBarProps {
  onMenuToggle?: () => void;
}

export function TopBar({ onMenuToggle }: TopBarProps) {
  const { user, authenticated, login, logout } = useAuth();
  // Source the org name from OrganizationContext, not from the JWT — the JWT's
  // `org_name` claim is baked in at login and doesn't update when the user
  // renames their org (or switches to a different one). OrganizationContext
  // is refreshed after rename/switch, so it always reflects the current
  // active org.
  const { activeOrganization } = useOrganization();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    if (menuOpen) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [menuOpen]);

  return (
    <header className="topbar">
      <div className="topbar-left">
        <button 
          className="topbar-menu-btn" 
          onClick={onMenuToggle}
          aria-label="Toggle menu"
        >
          <span className="topbar-menu-icon">☰</span>
        </button>
        <h1 className="topbar-heading">Haven</h1>
      </div>
      <div className="topbar-right">
        {authenticated && user ? (
          <>
            <OrganizationSwitcher />
            <div className="topbar-user" ref={menuRef}>
            <button
              className="topbar-avatar-btn"
              onClick={() => setMenuOpen((o) => !o)}
              aria-haspopup="true"
              aria-expanded={menuOpen}
            >
              <span className="topbar-avatar">
                {user.name?.charAt(0).toUpperCase() || "U"}
              </span>
              <span className="topbar-username">{user.name || user.email}</span>
              <span className="topbar-caret">▾</span>
            </button>
            {menuOpen && (
              <div className="topbar-dropdown">
                <div className="topbar-dropdown-header">
                  <div className="topbar-dropdown-email">{user.email}</div>
                  {activeOrganization?.name && (
                    <div className="topbar-dropdown-org">{activeOrganization.name}</div>
                  )}
                </div>
                <div className="topbar-dropdown-divider" />
                <button
                  className="topbar-dropdown-item"
                  onClick={() => {
                    setMenuOpen(false);
                    navigate("/profile");
                  }}
                >
                  Profile
                </button>
                <div className="topbar-dropdown-divider" />
                <button
                  className="topbar-dropdown-item"
                  onClick={() => {
                    setMenuOpen(false);
                    logout();
                  }}
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
          </>
        ) : (
          <button className="topbar-login-btn" onClick={login}>
            Sign in
          </button>
        )}
      </div>
    </header>
  );
}
